import { createSelector } from 'reselect'
import { IssuerIds } from 'src/enums'
import { BulkJobModel, BulkUploadResponse } from 'src/models/bulkUpload.models'
import { UserConnectionResponse } from 'src/models/people.models'
import {
  VirtualCardListModel,
  VirtualCardListResponse,
  VirtualCardSecureData,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import {
  VirtualCardPermissionsModel,
  VirtualCardPermissionsResponse,
} from 'src/models/virtualCardPermissions.models'
import { RootState } from 'src/redux/createRootReducer'

export const selectVirtualCardListResponsesFromState = (
  state: RootState,
): Record<string, VirtualCardListResponse> =>
  state.data.virtualCards.listResponses
export const selectVirtualCardListModels = createSelector(
  [selectVirtualCardListResponsesFromState],
  (virtualCardListResponses) =>
    Object.values(virtualCardListResponses).reduce<
      Record<VirtualCardListResponse['id'], VirtualCardListModel>
    >(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new VirtualCardListModel(curr),
      }),
      {},
    ),
)

export const selectAmexVirtualCardListModelsFromState = createSelector(
  [selectVirtualCardListModels],
  (virtualCardListModels) =>
    Object.values(virtualCardListModels).filter(
      (vc) =>
        vc.issuer !== undefined &&
        (vc.issuer.id === IssuerIds.Amex ||
          vc.issuer.id === IssuerIds.AmexDemo),
    ),
)

export const selectVirtualCardBulkJobsFromState = (
  state: RootState,
): Record<string, BulkUploadResponse> => state.data.virtualCards.bulkJobs
export const selectVirtualCardBulkJobModels = createSelector(
  [selectVirtualCardBulkJobsFromState],
  (bulkJobResponses) =>
    Object.values(bulkJobResponses || {}).reduce<
      Record<BulkUploadResponse['id'], BulkJobModel>
    >(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new BulkJobModel(curr),
      }),
      {},
    ),
)

const selectVirtualCardSecureDataFromState = (
  state: RootState,
): Record<string, VirtualCardSecureData> => state.data.virtualCards.secureData
export const selectVirtualCardSecureModels = createSelector(
  [
    selectVirtualCardListResponsesFromState,
    selectVirtualCardSecureDataFromState,
  ],
  (virtualCardListResponses, virtualCardSecureData) =>
    Object.values(virtualCardListResponses).reduce(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new VirtualCardSecureModel({
          ...curr,
          vcn: virtualCardSecureData[curr.id]?.vcn,
          securityCode: virtualCardSecureData[curr.id]?.securityCode,
        }),
      }),
      {} as Record<string, VirtualCardSecureModel>,
    ),
)

export const selectVirtualCardPermissionsResponsesFromState = (
  state: RootState,
): Record<string, VirtualCardPermissionsResponse> =>
  state.data.virtualCards.permissions
export const selectVirtualCardPermissionsModelsFromState = createSelector(
  [selectVirtualCardPermissionsResponsesFromState],
  (virtualCardPermissions) =>
    Object.entries(virtualCardPermissions).reduce(
      (acc, curr) => ({
        ...acc,
        [curr[0]]: new VirtualCardPermissionsModel(curr[1]),
      }),
      {} as Record<string, VirtualCardPermissionsModel>,
    ),
)

export const selectVirtualCardStatisticsClearedTransactionDebitAmountFromState =
  (state: RootState): Record<string, number> =>
    state.data.virtualCards.clearedTransactionDebitAmount

export const selectVirtualCardStatisticsClearedTransactionCreditAmountFromState =
  (state: RootState): Record<string, number> =>
    state.data.virtualCards.clearedTransactionCreditAmount

export const selectVirtualCardClearedTransactionAmountFromState =
  createSelector(
    [
      selectVirtualCardStatisticsClearedTransactionDebitAmountFromState,
      selectVirtualCardStatisticsClearedTransactionCreditAmountFromState,
    ],
    (debitAmount, creditAmount) => {
      return debitAmount !== undefined && creditAmount !== undefined
        ? Object.entries(debitAmount).reduce(
            (acc, curr) => {
              return {
                ...acc,
                [curr[0]]: debitAmount[curr[0]] - creditAmount[curr[0]],
              }
            },
            {} as Record<string, number>,
          )
        : undefined
    },
  )

export const selectCardCreatorFromState = (
  state: RootState,
): UserConnectionResponse | undefined => state.data.virtualCards.cardCreator

export const selectSelectedVirtualCardIdsFromState = (
  state: RootState,
): string[] | undefined => state.data.virtualCards.selectedVirtualCardIds
